<template>
  <q-card
    :id="gridParentId"
    :flat="flatCard"
    text-color="dark-grey"
    class="relative-position bg-white no-border-radius ag-grid-wrapper-card"
  >
    <slot name="header-row" />
    <q-card-section
      v-if="(title && !titleInset) || includeSearchBox || actions.length > 0 || hasExportExcel"
      :class="cardTitleClass"
      class="flex justify-between table-title"
    >
      <slot name="top-left-content">
        <div v-if="title && !titleInset" class="box__title__boxed float-left self-center">
          {{ title }}
        </div>
      </slot>
      <slot name="top-right-content" />
      <div :class="{ 'float-right': title, 'float-left': !title }" class="self-center">
        <slot name="headerActions">
          <template v-if="includeSearchBox">
            <lm-index-page-list-bar @search="onSearch" />
          </template>
        </slot>
      </div>
      <div v-if="actions.length || hasCustomColumnFilter || hasExportExcel" class="actions">
        <slot name="header-actions-before"></slot>
        <div v-for="(action, index) of actions" :key="`top_action_${index}`">
          <q-btn
            v-if="action.type === 'route'"
            :key="`action_${index}`"
            :to="{ name: action.routeName }"
            :label="action.label"
            :disable="action.disabled"
            :color="action.color || 'tertiary'"
            size="sm"
            class="on-right"
          />
          <q-btn
            v-else
            :key="`action_${index}`"
            :label="action.label"
            :disable="action.disabled"
            :color="action.color || 'tertiary'"
            size="sm"
            class="on-right"
            @click="$emit(action.event)"
          />
        </div>
        <q-btn v-if="hasCustomColumnFilter" round flat size="sm" color="tertiary" icon="far fa-columns">
          <LmTooltip>
            {{ getTranslation('lists.visible_columns') || 'Select visible columns' }}
          </LmTooltip>
          <q-menu>
            <q-list bordered padding style="min-width: 300px">
              <q-item-label header>Filter columns</q-item-label>
              <q-item v-for="(column, index) of columns" :key="column.name + index" clickable>
                <q-item-section>
                  <q-item-label>
                    <q-checkbox
                      v-model="reactiveColumns"
                      :val="column.value"
                      :label="column.name"
                      color="tertiary"
                      @input="toggleColumnVisibility($event, column.value)"
                    />
                  </q-item-label>
                </q-item-section>
              </q-item>
            </q-list>
          </q-menu>
        </q-btn>
        <q-btn
          v-if="hasExportExcel"
          :loading="isExportLoading"
          round
          flat
          size="sm"
          color="tertiary"
          icon="fas fa-file-spreadsheet"
          @click="onExportToExcel"
        >
          <LmTooltip> Export to Excel </LmTooltip>
        </q-btn>
      </div>
    </q-card-section>
    <q-separator v-if="separator" :class="cardSeparatorClass" class="card__separator" />
    <q-card-section :class="cardContentClass" class="table-content">
      <div v-if="title && titleInset" class="inset-card-title q-pt-md">
        {{ title }}
        <slot name="inset-actions" />
      </div>
      <slot name="customContent" />
      <ag-grid-vue
        :grid-options="gridOptions"
        :class="[agGridClass, { 'has-filter': gridOptions.floatingFilter }]"
        :popup-parent="gridParent"
        :style="gridStyle"
        @grid-ready="gridReady"
      />
      <div v-if="gridOptions.pagination" class="row no-wrap items-center justify-center q-mt-lg">
        <form @submit.prevent="handlePageSize(paginationControls.pageSize)">
          <q-input
            ref="rowsPerPage"
            v-model.number="paginationControls.pageSize"
            style="max-width: 80px"
            type="number"
            class="q-mr-md rows-per-page"
            outlined
            dense
            @focus="$refs.rowsPerPage.select()"
          />
        </form>
        <span
          >{{ paginationControls.showingFirst }} to {{ paginationControls.showingLast }} of
          {{ paginationControls.rowCount }}</span
        >
        <q-btn
          :disable="paginationControls.currentPage === 1"
          size="xs"
          flat
          icon="fas fa-chevron-double-left"
          class="q-ml-xs"
          @click="goToFirst"
        />
        <q-btn
          :disable="paginationControls.currentPage === 1"
          size="xs"
          flat
          icon="fas fa-chevron-left"
          class="q-mr-md"
          @click="goToPrevious"
        />
        <span>page {{ paginationControls.currentPage }} / {{ paginationControls.totalPages }}</span>
        <q-btn
          :disable="paginationControls.totalPages === paginationControls.currentPage"
          size="xs"
          flat
          icon="fas fa-chevron-right"
          class="q-ml-md"
          @click="goToNext"
        />
        <q-btn
          :disable="paginationControls.totalPages === paginationControls.currentPage"
          size="xs"
          flat
          icon="fas fa-chevron-double-right"
          class="q-ml-xs"
          @click="goToLast"
        />
      </div>
    </q-card-section>
    <q-card-actions>
      <slot name="footerActions" />
    </q-card-actions>
  </q-card>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue'
import { LmGridOptions } from '@lib/models/lm-grid-options.class'
import { uid } from 'quasar'
import LmIndexPageListBar from '@components/IndexPageListBar'
import { isEqual, cloneDeep, get } from 'lodash'
import { isNumber } from '@/lib/generic.utils'

export default {
  name: 'AgGridWrapper',
  components: {
    AgGridVue,
    LmIndexPageListBar
  },
  props: {
    options: {
      type: Object,
      default: () => {
        return {}
      }
    },
    serviceNamespace: {
      type: String,
      default: () => {
        return 'data'
      }
    },
    storeNamespace: {
      type: String,
      default: () => {
        return 'data'
      }
    },
    title: {
      type: String,
      default: null
    },
    separator: {
      type: Boolean,
      default: true
    },
    includeSearchBox: {
      type: Boolean,
      default: false
    },
    cardClass: {
      type: String,
      default: 'bg-white'
    },
    cardTitleClass: {
      type: String,
      default: ''
    },
    cardContentClass: {
      type: String,
      default: ''
    },
    cardSeparatorClass: {
      type: String,
      default: ''
    },
    agGridClass: {
      type: String,
      default: 'ag-theme-material'
    },
    isImport: {
      type: Boolean,
      default: false
    },
    importFile: {
      type: String,
      default: ''
    },
    needsUpdatePreprocessing: {
      type: Boolean,
      default: false
    },
    flatCard: {
      type: Boolean,
      default: false
    },
    actions: {
      type: Array,
      default: () => {
        return []
      }
    },
    gridStyle: {
      type: Object,
      default: null
    },
    titleInset: {
      type: Boolean,
      default: false
    },
    preselectRows: {
      type: Boolean,
      default: false
    },
    hasComputedColumns: {
      type: Boolean,
      default: false
    },
    searchAllData: {
      type: Boolean,
      default: false
    },
    hasCustomColumnFilter: Boolean,
    visibleColumns: Array,
    hasExportExcel: Boolean,
    handleUpdateOnParent: Boolean,
    noResize: Boolean,
    infiniteScrollRequest: Function,
    updateRowsCount: Function,
    handleColumnVisibilityOnParent: Boolean
  },
  data() {
    return {
      gridOptions: new LmGridOptions(this.options, this.noResize, this.infiniteScrollRequest, this.updateRowsCount),
      selected: null,
      selectedItems: null,
      gridParentId: uid(),
      gridParent: null,
      paginationControls: {
        pageSize: null,
        rowCount: null,
        currentPage: null,
        totalPages: null,
        showingFirst: null,
        showingLast: null
      },
      delay: 500,
      reactiveColumns: [],
      isExportLoading: false
    }
  },
  computed: {
    columns() {
      if (!this.hasCustomColumnFilter) return
      let options = this.options.columnDefs.filter(option => !option.lockVisible)
      return options.map(option => ({
        name: option.headerName,
        value: option.field,
        visible: !option.hide
      }))
    }
  },
  watch: {
    options(newVal, oldVal) {
      if (newVal !== oldVal) {
        !this.infiniteScrollRequest && this.gridOptions.api.setRowData(this.options.rowData)
        this.preselectGridNodes()
      }
    }
  },
  created() {
    this.$on('create', event => {
      return this.addRow(event)
    })
    this.$on('delete', () => {
      return this.onRemoveSelected()
    })
    this.$on('update', event => {
      return this.cellUpdate(event)
    })

    if (!this.noResize) {
      this.$root.$on('resize', () => {
        if (this.gridOptions && this.gridOptions.api) {
          this.gridOptions.api.checkGridSize()
          this.gridOptions.api.sizeColumnsToFit()
        }
      })
    }

    if (this.hasCustomColumnFilter) this.reactiveColumns = [...this.visibleColumns]
  },
  beforeMount() {
    this.gridOptions.onDragStopped = params => {
      this.$emit('drag-stopped', params)
    }
    this.gridOptions.onSelectionChanged = this.refreshSelectedRows
    this.gridOptions.onRowSelected = this.rowSelectionChanged
    this.gridOptions.onCellValueChanged = this.isImport
      ? this.importCellUpdate
      : this.needsUpdatePreprocessing || this.hasComputedColumns
      ? this.handleCellUpdateRequest
      : this.cellUpdate
    this.onPaginationChanged()
    this.gridOptions.onPaginationChanged = this.onPaginationChanged
  },
  mounted() {
    this.$set(this, 'gridParent', document.getElementById(this.gridParentId))
    this.preselectGridNodes()
  },
  methods: {
    gridReady({ api, columnApi }) {
      this.$emit('grid-ready', { api, columnApi })
    },

    onPaginationChanged() {
      if (this.gridOptions.api) {
        let toRows =
          (this.gridOptions.api.paginationGetCurrentPage() + 1) * this.gridOptions.api.paginationGetPageSize()
        this.$set(this, 'paginationControls', {
          pageSize: this.gridOptions.api.paginationGetPageSize(),
          rowCount: this.gridOptions.api.paginationGetRowCount(),
          currentPage: this.gridOptions.api.paginationGetCurrentPage() + 1,
          totalPages: this.gridOptions.api.paginationGetTotalPages(),
          showingFirst:
            this.gridOptions.api.paginationGetCurrentPage() * this.gridOptions.api.paginationGetPageSize() + 1,
          showingLast:
            toRows > this.gridOptions.api.paginationGetRowCount()
              ? this.gridOptions.api.paginationGetRowCount()
              : toRows
        })
      }
    },
    handlePageSize(val) {
      this.gridOptions.api.paginationSetPageSize(val)
    },
    goToFirst() {
      this.gridOptions.api.paginationGoToFirstPage()
    },
    goToPrevious() {
      this.gridOptions.api.paginationGoToPreviousPage()
    },
    goToNext() {
      this.gridOptions.api.paginationGoToNextPage()
    },
    goToLast() {
      this.gridOptions.api.paginationGoToLastPage()
    },
    selectAllRows() {
      this.gridOptions.api.selectAll()
    },
    deselectAllRows() {
      this.gridOptions.api.deselectAll()
    },
    preselectGridNodes() {
      if (this.selectedItems && this.selectedItems.length > 0) {
        this.selected = this.selectedItems.map(item => {
          return item.id
        })
      }
      if (this.preselectRows) {
        let sel = []
        this.gridOptions.rowData.forEach(item => {
          if (item.preselected === true) sel.push(item.id)
        })
        this.$set(this, 'selected', sel)
      }
      if (this.selected && this.selected.length > 0) {
        this.gridOptions.api.forEachNode(item => {
          item.setSelected(this.selected.indexOf(item.data.id) > -1)
        })
      }
    },
    onSearch(terms) {
      return this.searchAllData ? this.searchCompleteData(terms) : this.gridOptions.api.setQuickFilter(terms)
    },
    searchCompleteData(terms) {
      let results = this.options.rowData.filter(
        obj =>
          JSON.stringify(obj)
            .toLowerCase()
            .indexOf(terms.toLowerCase()) !== -1
      )
      this.gridOptions.api.setRowData(results)
    },
    handleCellUpdateRequest(event) {
      const newVal = event.newValue || null
      const oldVal = event.oldValue || null
      if (isEqual(newVal, oldVal)) return

      let payload = {
        item: event.data,
        field: event.colDef.field,
        updated: event.value,
        originalEvent: event
      }
      return this.$emit('requested-update', payload)
    },
    cellUpdate(event) {
      if (!this.needsUpdatePreprocessing) {
        const newVal = event.newValue || null
        const oldVal = event.oldValue || null
        if (isEqual(newVal, oldVal)) return
      }

      let obj = this.needsUpdatePreprocessing ? cloneDeep(event) : cloneDeep(event.data)
      let params = {
        namespace: this.serviceNamespace,
        id: obj.id,
        value: obj
      }
      if (this.handleUpdateOnParent) {
        this.$emit('cell-updated', event)
        return
      }
      this.$store
        .dispatch(`${this.storeNamespace}/update`, params)
        .then(result => {
          this.updateRows([result])
          this.$emit('refresh-data', result)
          // DONT delete until making sure no side effects are produced
          // let data = cloneDeep(this.options.rowData)
          // let index = findIndex(data, { id: result.id })
          // if (index > -1) data.splice(index, 1, result)
          // this.$set(this.options, 'rowData', data)
          // this.gridOptions.api.setRowData(data)
          // this.$emit('refresh-data', result)
        })
        .catch(() => {
          const row = this.options.rowData.find(item => item.id === obj.id)
          if (row) this.updateRows([row])
        })
    },
    importCellUpdate(event) {
      if (this.handleUpdateOnParent) {
        this.$emit('cell-updated', event)
        return
      }
      const newVal = cloneDeep(event.newValue) || null
      const oldVal = cloneDeep(event.oldValue) || null
      if (isNumber(newVal) && Number(newVal) === Number(oldVal)) return
      if (isEqual(newVal, oldVal)) return

      let obj = {
        file: this.importFile,
        value: get(event.data, event.column.colId),
        field: event.colDef.field
      }
      let params = {
        namespace: this.serviceNamespace,
        id: event.data.id,
        value: obj
      }
      return this.$store.dispatch(`${this.storeNamespace}/previewImport`, params)
    },
    refreshSelectedRows() {
      if (!this.gridOptions.api) return
      const selRows = this.gridOptions.api.getSelectedRows()
      this.selected = selRows.map(row => row.id)
      this.$emit('selectedRows', this.selected) // providing only ids
      this.$emit('selected-rows-entire-row', selRows) // providing the entire row
    },
    rowSelectionChanged(event) {
      this.$emit('row-selection-changed', event)
    },
    updateSelectedRows() {
      this.$nextTick(() => {
        this.gridOptions.api.forEachNode(node => {
          if (node.data.preselected) {
            node.setSelected(true)
          } else {
            node.setSelected(false)
          }
        })
        this.refreshSelectedRows()
      })
    },
    // TODO: Find out if this is still used anywhere
    removeSelectedRow(id, row) {
      // TODO replace native alert with Dialog
      return this.$store
        .dispatch(`${this.storeNamespace}/deleteSingle`, {
          namespace: `${this.serviceNamespace}`,
          id: id
        })
        .then(() => {
          this.gridOptions.api.applyTransaction({ remove: [row] })
        })
    },
    onRemoveSelected() {
      const selectedData = this.gridOptions.api.getSelectedRows()
      if (selectedData.length) {
        if (confirm('Are you sure?')) {
          selectedData.forEach(row => {
            this.removeSelectedRow(row.id, row)
          })
        }
      }
    },
    getAllRowsAfterFilter() {
      let rows = []
      this.gridOptions.api.forEachNodeAfterFilter(node => rows.push(node.data))
      return rows
    },
    addRow(event) {
      let params = {
        namespace: `${this.serviceNamespace}`,
        value: event
      }
      return this.$store.dispatch(`${this.storeNamespace}/create`, params).then(result => {
        if (!get(result, 'errors')) {
          this.gridOptions.api.applyTransaction({ add: [result] })
          this.$emit('created', result)
        }
      })
    },

    toggleColumnVisibility(visibleColumns, toggledColumn) {
      const toggle = visibleColumns.includes(toggledColumn)
      if (this.handleColumnVisibilityOnParent) {
        this.$emit('toggle-column-visibility', {
          visibleColumns,
          toggledColumn,
          toggle
        })
        return
      }
      this.gridOptions.columnApi.setColumnVisible(toggledColumn, toggle)
    },

    toggleVisibleColumns(columns, makeVisible = true) {
      this.gridOptions.columnApi.setColumnsVisible(columns, makeVisible)
    },

    toggleVisibleColumn(column, toggle) {
      this.gridOptions.columnApi.setColumnVisible(column, toggle)
    },

    setBottomPinnedData(data) {
      this.gridOptions.api.setPinnedBottomRowData(data ? [data] : [])
    },

    setTopPinnedData(data) {
      this.gridOptions.api.setPinnedTopRowData(data ? [data] : [])
    },

    setColumnPinned(column, direction) {
      this.gridOptions.columnApi.setColumnPinned(column, direction)
    },

    setRows(rows) {
      this.gridOptions.api.setRowData(rows)
    },

    updateRows(rows) {
      this.gridOptions.api.applyTransaction({ update: rows })
    },

    onExportToExcel() {
      this.$emit('export-to-excel', this.gridOptions)
    },

    purgeServerSideCache() {
      this.gridOptions.api.refreshServerSideStore({ purge: true })
    },

    updateRowNode(nodeId, newData) {
      const node = this.gridOptions.api.getRowNode(nodeId)
      node.setData(newData)
    },

    setExportLoadingState(loadingState) {
      this.isExportLoading = loadingState
    },

    getGridOptions() {
      return this.gridOptions
    },

    setLoading(state) {
      if (state) this.gridOptions.api.showLoadingOverlay()
      else this.gridOptions.api.hideOverlay()
    }
  }
}
</script>
<style lang="stylus" scoped>
  @import '~@/styles/stylus/custom-variables'

.ag-theme-material {
  font-family "Barlow", sans-serif
}

.ag-row-even {
  background transparent !important
}

.table-title {
  padding 15px 20px 3px 20px!important
  font-size 18px
}

.table-content {
  padding 20px 40px
}

.actions {
  display: grid
  grid-auto-flow column
  gap 10px
}

.rows-per-page {
  margin-top: -3px !important
  .q-input-target {
    margin-top: 0 !important;
    height: auto !important;
  }
}

>>> .ag-theme-material:not(.has-filter) .ag-header {
  border-bottom 1px solid #dcdcdc !important
}

>>> .ag-theme-material .ag-header {
  .ag-header-row:nth-child(2) {
    background white
  }

  .ag-header-cell {
    display flex
  }

  .ag-header-row:first-child > .ag-header-cell {
    &:after {
      border-right 1px solid rgba(189, 195, 199, 0.5)
      content " "
      height 50%
      margin-top 15px
      position absolute
      top 0
      right 5px
    }
  }

  .ag-floating-filter-body input, .ag-floating-filter-full-body input {
    border 1px solid $light-grey
    border-radius 5px
    margin 5px
    padding 5px
    height 30px
  }

  .ag-floating-filter-body input:focus {
    border-bottom: 2px solid $primary !important;
  }

  .ag-floating-filter-button {
    height 100%
    align-items center
    margin-left 0 !important
    margin-top -8px
  }
}

>>> .ag-header-row {
  background white
  font-size 12px
  font-weight 500
  color #333333
  font-family "Barlow", sans-serif
}

>>> .ag-theme-material .ag-cell {
  line-height 36px
  padding-left 5px !important

  input {
    height 28px !important
  }
}

>>> .ag-header-cell {
  padding 0 !important
}

>>> .ag-body-viewport.ag-layout-auto-height .ag-center-cols-clipper, .ag-body-viewport.ag-layout-auto-height .ag-center-cols-container {
  min-height 36px !important
}
</style>
